// A stage's cards: compact StoreRows that open the store (/stores/:id). Short lists render plainly; long ones
// (boardModel VIRTUALIZE_AFTER) go through TanStack Virtual so a 5,000-store "New" column costs a screenful of DOM.
// Cards vary in height (two-line names, card fields), so rows are measured as they render.

import { useVirtualizer } from '@tanstack/react-virtual';
import { useRef } from 'react';
import type { RecordPayload } from '@/contracts/models';
import type { DataItem } from '@/data/hooks';
import { StoreRow, type StoreRowLookups } from '@/features/common';
import { cn } from '@/ui';

/** A compact card is about this tall with a one-line name and subtitle; measuring corrects the rest. */
const ESTIMATED_CARD_HEIGHT = 112;
const CARD_GAP = 8;
const LIST_PADDING = 8;

export interface CardListProps {
  records: readonly RecordPayload[];
  /** `useVisibleRecords().get`: StoreRow wants the item (row meta, newer-app flag), the board has payloads. */
  itemOf: (id: string) => DataItem<RecordPayload> | undefined;
  lookups: StoreRowLookups;
  /** Accessible name of the list ("Stores in Proposal"). */
  label: string;
  virtualized: boolean;
  /** Sizing of the scroll box: a board column fills its column, the dialog gives it a fixed height. */
  className?: string;
}

export function CardList({ virtualized, ...props }: CardListProps) {
  return virtualized ? <VirtualCardList {...props} /> : <PlainCardList {...props} />;
}

type ListProps = Omit<CardListProps, 'virtualized'>;

function Card({ record, itemOf, lookups }: { record: RecordPayload } & Pick<ListProps, 'itemOf' | 'lookups'>) {
  const item = itemOf(record.id);
  if (!item) return null;
  return <StoreRow variant="compact" item={item} lookups={lookups} href={`/stores/${item.id}`} />;
}

function PlainCardList({ records, itemOf, lookups, label, className }: ListProps) {
  return (
    <div className={cn('min-h-0 overflow-y-auto overscroll-contain gt-scroll', className)}>
      <ul role="list" aria-label={label} className="flex flex-col p-2" style={{ gap: CARD_GAP }}>
        {records.map((r) => (
          <li key={r.id}>
            <Card record={r} itemOf={itemOf} lookups={lookups} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function VirtualCardList({ records, itemOf, lookups, label, className }: ListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: records.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_CARD_HEIGHT,
    getItemKey: (i) => records[i]?.id ?? i,
    gap: CARD_GAP,
    paddingStart: LIST_PADDING,
    paddingEnd: LIST_PADDING,
    // Generous overscan keeps the next cards in the DOM, so Tab moves on to them as the column scrolls.
    overscan: 8,
  });
  const total = records.length;

  return (
    <div ref={scrollRef} className={cn('min-h-0 overflow-y-auto overscroll-contain gt-scroll', className)}>
      <ul role="list" aria-label={label} className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
        {virtualizer.getVirtualItems().map((v) => {
          const record = records[v.index];
          if (!record) return null;
          return (
            <li
              key={v.key}
              data-index={v.index}
              ref={virtualizer.measureElement}
              // Only a window of the list is in the DOM: tell assistive tech where this card sits in the whole column.
              aria-setsize={total}
              aria-posinset={v.index + 1}
              className="absolute top-0 right-2 left-2"
              style={{ transform: `translateY(${v.start}px)` }}
            >
              <Card record={record} itemOf={itemOf} lookups={lookups} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
